import { FC } from 'react'
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import R4RIcon from 'components/R4RIcon'
import { WidgetCard, WidgetCardHeader, WidgetCardBody } from './WidgetCard'
import { useLeaseContractsList } from 'services/contracts/hooks/useLeaseContracts'
import { useUserFlats } from 'services/contracts/hooks/useUserFlats'
import { useUserRoles, isOwner } from 'services/rbac/useUserRoles'
import { CONTRACT_STATUS } from 'services/contracts/contracts.consts'

const REPORTABLE_CONTRACT_STATUSES = [
  CONTRACT_STATUS.contractStatusActivationReady,
  CONTRACT_STATUS.contractStatusTypeActive,
  CONTRACT_STATUS.contractStatusDepositPaid,
  CONTRACT_STATUS.contractStatusEnded
]

const WidgetFault: FC = () => {
  const navigate = useNavigate()
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const { leaseContractsList } = useLeaseContractsList()
  const { userFlats } = useUserFlats()
  const userRoles = useUserRoles()
  const isOwnerUser = isOwner(userRoles)

  const hasNonFrozenContract = leaseContractsList.some(
    ({ leaseStatus, isOperationsFrozen }) =>
      !isOperationsFrozen && REPORTABLE_CONTRACT_STATUSES.includes(leaseStatus)
  )

  const hasNonFrozenOwnerFlat = Boolean(
    isOwnerUser &&
      userFlats &&
      userFlats.some(
        (flat) => flat.connectionType === 'owner' && !flat.investment?.operationsFrozen
      )
  )

  if (!hasNonFrozenContract && !hasNonFrozenOwnerFlat) {
    return null
  }

  if (isDesktop) {
    return (
      <WidgetCard sx={{ flex: 1, minWidth: 0 }}>
        <WidgetCardHeader
          icon='construct'
          title={formatMessage({ id: 'discounts.helpDesk', defaultMessage: 'HelpDesk' })}
        />
        <WidgetCardBody sx={{ gap: '14px', justifyContent: 'space-between' }}>
          <Typography
            sx={{ fontSize: '13px', color: theme.r4r.navy, whiteSpace: 'pre-line' }}>
            {formatMessage({
              id: 'faultWidget.description',
              defaultMessage:
                'W Twoim lokalu coś się wydarzyło?\nKliknij w przycisk aby zgłosić nam to!'
            })}
          </Typography>
          <Button
            fullWidth
            onClick={() => navigate('/faults/report/add')}
            startIcon={<R4RIcon name='add' size={16} />}
            sx={{
              backgroundColor: theme.r4r.navy,
              color: '#fff',
              fontSize: '14px',
              fontWeight: 600,
              padding: '12px 16px',
              borderRadius: 0,
              '&:hover': { backgroundColor: theme.r4r.ink }
            }}>
            {formatMessage({ id: 'faultWidget.reportFault', defaultMessage: 'Zgłoś usterkę' })}
          </Button>
        </WidgetCardBody>
      </WidgetCard>
    )
  }

  return (
    <WidgetCard sx={{ padding: '14px 18px 24px', gap: '14px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: theme.r4r.navy }}>
        <R4RIcon name='construct' size={16} />
        <Typography sx={{ fontSize: '15px', fontWeight: 700, color: theme.r4r.navy }}>
          {formatMessage({ id: 'discounts.helpDesk', defaultMessage: 'HelpDesk' })}
        </Typography>
      </Box>
      <Button
        fullWidth
        onClick={() => navigate('/faults/report/add')}
        startIcon={<R4RIcon name='add' size={18} />}
        sx={{
          backgroundColor: theme.r4r.navy,
          color: '#fff',
          fontSize: '13px',
          fontWeight: 500,
          minHeight: '28px',
          padding: '5px 12px',
          borderRadius: 0,
          '&:hover': { backgroundColor: theme.r4r.ink }
        }}>
        {formatMessage({ id: 'faultWidget.reportFault', defaultMessage: 'Zgłoś usterkę' })}
      </Button>
    </WidgetCard>
  )
}

export default WidgetFault
